<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { Ref } from "vue";
import VModal from "@magnit/core/src/components/VModal/VModal.vue";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VBlockLoader from "@magnit/core/src/components/VLoader/VBlockLoader.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import FiltersGridFull from "./GridFull.vue";
import FiltersCountClear from "./CountClear.vue";
import FiltersDiscount from "./Discount.vue";
import FiltersRange from "./Range.vue";
import FiltersGrid from "./Grid.vue";
import FiltersCategoriesMobile from "./CategoriesMobile.vue";
import ArrowLeftIcon from "@magnit/icons/src/assets/icons/arrow-left-corner-24.svg";
import { cloneDeep, isEqual } from "lodash-es";
import type { ICategory } from "@magnit/unit-catalog/src/unit-catalog.types";
import type {
  ICategoriesFunctions,
  IGoodsFilter,
  IGoodsFilters,
  IRequestGoodsFilters,
  IFilterFunctions,
} from "../types";
import type { IAnyObject } from "../types/object";
import { pluralGoods } from "../utils/plural";
import { shallowDiff } from "../utils/object";
import type { IProduct } from "@magnit/unit-catalog/src/unit-catalog.types";
import type { IPaginationProps } from "@magnit/core/src/containers/VPagination/VPagination.types";
import { useAnalyticManager } from "@magnit/analytic-events";

const modelValue = defineModel<IAnyObject>({ default: {} });

const props = defineProps<{
  //modal state
  name: string;
  isActive: boolean;
  isOpen: boolean;
  // misc state
  category: ICategory;
  storeCode: string;
  filters?: IGoodsFilters | null;
  countChange?: number;
  defaultFilters: IAnyObject;
  categoriesStore: ICategoriesFunctions;
  // requesters
  requestFilters: (
    params: IRequestGoodsFilters,
    key?: string | undefined,
  ) => Promise<{
    data: Ref<IGoodsFilters | null>;
    status: Ref<"idle" | "pending" | "success" | "error">;
  }>;
  requestPromotions?: (
    params: {
      categoryId?: string | number;
      storeCode?: string;
      adult?: boolean;
    },
    key?: string | undefined,
  ) => Promise<{
    data: Ref<{ goods: IProduct[]; pagination: IPaginationProps } | null>;
    status: Ref<"idle" | "pending" | "success" | "error">;
  }>;
  // "functions" providers
  catalogFilterFunctions: IFilterFunctions;
  hideCategories?: boolean;

  analyticsSender?: (name: string, params?: Record<string, unknown>) => void;
  analyticsPayload?: Record<string, unknown>;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { send: nativeSend } = useAnalyticManager();
const send = props.analyticsSender || nativeSend;

const modalClose = () => {
  emit("close");
};

const isLoading = ref(false);
const innerValue = ref(cloneDeep(modelValue.value));
const innerFilters = ref(cloneDeep(props.filters));
const innerDefaultFilters = ref(cloneDeep(props.defaultFilters));
const currentFilter = ref<IGoodsFilter | null>(null);
const totalProducts = ref(innerFilters.value?.pagination.totalCount || 0);
const prevFiltersPayload = ref<Partial<IRequestGoodsFilters> | null>(null);

watch(
  modelValue,
  (next) => {
    innerValue.value = cloneDeep(next);
  },
  {
    deep: true,
  },
);
watch(
  () => props.defaultFilters,
  (next) => {
    innerDefaultFilters.value = cloneDeep(next);
  },
  {
    deep: true,
  },
);

const onChangeFilter = async (categoryKey?: string) => {
  isLoading.value = true;

  const categoryId = props.categoriesStore.getCategoryByKey(
    categoryKey || innerValue.value.categoryKey,
  )?.id;
  if (
    props.categoriesStore.getTypeByKey(
      categoryKey || innerValue.value.categoryKey,
    ) === "goods"
  ) {
    const payloadFilters = {
      categoryIDs: categoryId
        ? props.categoriesStore.getCategoryDescendants([categoryId])
        : [],
      storeCodes: [props.storeCode],
      ...(categoryKey
        ? null
        : props.catalogFilterFunctions.getFiltersPayload(
            innerFilters.value?.filters || [],
            innerValue.value,
          )),
    };
    if (isEqual(prevFiltersPayload.value, payloadFilters)) {
      isLoading.value = false;
      return;
    }
    prevFiltersPayload.value = payloadFilters;
    // TODO: вообще работу с фильтрами вытащить бы вне компонента ДС? Чтобы мы получали просто готовый набор фильтров
    // Пропом получать фильтры -- событием уведомлять что фильтры надо перестроить.
    // Остальная ответственность уже на интегрирующей стороне.
    // Пока-что сделал затычку прокидку функции через проп для более быстрого выноса компонента в ДС.
    const { data, status } = await props.requestFilters(
      payloadFilters,
      "filters-modal-count",
    );
    if (status.value === "success") {
      totalProducts.value = data.value?.pagination.totalCount || 0;
      if (categoryKey) {
        innerFilters.value = cloneDeep(data.value);
        const _filters = {
          ...Object.fromEntries(
            props.catalogFilterFunctions.getArrayFilters(
              innerFilters.value?.filters || [],
            ),
          ),
          onlyDiscount: false,
        };
        innerValue.value = {
          ...cloneDeep(_filters),
          categoryKey,
        };
        innerDefaultFilters.value = {
          ...cloneDeep(_filters),
          categoryKey: props.defaultFilters?.categoryKey,
        };
      }
    }
  } else if (props.requestPromotions) {
    const { data, status } = await props.requestPromotions({
      categoryId,
      storeCode: props.storeCode,
      adult: true,
    });
    if (status.value === "success") {
      totalProducts.value = data.value?.pagination.total || 0;
    }
  }
  isLoading.value = false;
};

const onChangeCategory = (categoryKey: string) => {
  onChangeFilter(categoryKey);
};

const innerCountChange = computed(
  () =>
    Object.keys(shallowDiff(innerDefaultFilters.value, innerValue.value))
      .length,
);
const innerCountChangeFromModel = computed(
  () => Object.keys(shallowDiff(modelValue.value, innerValue.value)).length,
);

const setCurrentFilter = (v: string) => {
  currentFilter.value =
    innerFilters.value?.filters.find((i) => i.id === v) || null;

  // Скроллим модалку вверх
  const modalWrap = window.document.getElementsByClassName(
    "wrap-unit-catalog-filters-modal",
  );
  if (modalWrap.length) {
    const content = modalWrap[0].getElementsByClassName(
      "pl-modal-content-wrapper",
    );
    if (content.length) {
      content[0].scrollTop = 0;
    }
  }
};

const submitText = computed(() => {
  return totalProducts.value
    ? `Показать ${totalProducts.value} ${pluralGoods(totalProducts.value)}`
    : "Товары не найдены";
});

const onSubmit = () => {
  modelValue.value = innerValue.value;
  send("Filter:List:Apply", { ...props.analyticsPayload });
  modalClose();
};
const onButtonClose = () => {
  send("Filter:List:Close", { ...props.analyticsPayload });
  modalClose();
};
const onReset = (toDefault?: boolean) => {
  /* Разница из-за того, что количество изменений считается как количество не "Пустых фильтров" */
  innerValue.value = toDefault
    ? cloneDeep(modelValue.value)
    : cloneDeep(props.defaultFilters);
  innerDefaultFilters.value = cloneDeep(props.defaultFilters);
  innerFilters.value = cloneDeep(props.filters);
  totalProducts.value = innerFilters.value?.pagination.totalCount || 0;
};
const onCloseModal = () => {
  modalClose();
  onReset(true);
};

watch(
  () => props.isOpen,
  () => {
    if (!props.isOpen) {
      return;
    }

    send("Filter:List:View", { ...props.analyticsPayload });
  },
);
</script>

<template>
  <VModal
    :id="name"
    :active="isActive"
    :open="isOpen"
    :closable="true"
    :type="{ xs: 'bottom-sheet', m: 'modal' }"
    :title="currentFilter?.title || 'Фильтры'"
    wrapper-class="wrap-unit-catalog-filters-modal"
    @close="onCloseModal"
  >
    <template #headerLeft>
      <VIcon v-if="currentFilter" @click="currentFilter = null">
        <ArrowLeftIcon />
      </VIcon>
    </template>
    <div class="unit-catalog-filters-modal">
      <VBlockLoader :show="isLoading" absolute only-overlay />
      <template v-if="currentFilter && currentFilter.type === 'grid'">
        <FiltersGridFull
          v-model="innerValue[currentFilter.id]"
          v-model:filter="currentFilter"
        />
      </template>
      <template v-else>
        <FiltersCountClear :count="innerCountChange" @reset="() => onReset()" />
        <FiltersCategoriesMobile
          v-if="!hideCategories"
          v-model="innerValue.categoryKey"
          :categories-store="categoriesStore"
          @update:model-value="onChangeCategory"
        />
        <template
          v-if="
            innerValue.categoryKey &&
            props.categoriesStore.getTypeByKey(innerValue.categoryKey) ===
              'goods'
          "
        >
          <FiltersDiscount
            v-model="innerValue.onlyDiscount"
            @update:model-value="() => onChangeFilter()"
          />

          <div v-for="filter in innerFilters?.filters" :key="filter.id">
            <FiltersRange
              v-if="
                filter.type === 'range' &&
                Object.keys(filter).includes('minValue')
              "
              v-model="innerValue[filter.id]"
              :filter="filter"
              @update:model-value="() => onChangeFilter()"
            />
            <FiltersGrid
              v-else-if="filter.type === 'grid' && !!filter.elements?.length"
              v-model="innerValue[filter.id]"
              :filter="filter"
              modal
              @show-all="setCurrentFilter(filter.id)"
              @update:model-value="() => onChangeFilter()"
            />
          </div>
        </template>
      </template>
    </div>

    <template #footer>
      <div id="teleport-unit-catalog-filters-modal-footer">
        <template v-if="!currentFilter">
          <VButton
            v-if="
              (countChange || innerCountChange) && innerCountChangeFromModel
            "
            :loading="isLoading"
            theme="primary"
            size="l"
            full-width
            :disabled="!totalProducts"
            @click="onSubmit"
          >
            {{ submitText }}
          </VButton>
          <VButton v-else theme="secondary" full-width @click="onButtonClose">
            Закрыть
          </VButton>
        </template>
      </div>
    </template>
  </VModal>
</template>

<style lang="postcss" scoped>
:global(.wrap-unit-catalog-filters-modal .pl-modal) {
  height: 100%;
}

/*
  TODO: отрефачить скроллы в модалках https://track.magnit.ru/browse/MAGWEB-5650
  или скорее всего вообще убрать эту костылизацию в рамках работ по https://track.magnit.ru/browse/MAGWEB-5989
*/
:global(.wrap-unit-catalog-filters-modal .pl-modal-content) {
  padding-left: 0;
  padding-right: 0;
}

:global(.wrap-unit-catalog-filters-modal .pl-modal-content-wrapper) {
  padding-left: var(--pl-unit-x5);
  padding-right: var(--pl-unit-x5);
}

.unit-catalog-filters-modal {
  padding: var(--pl-unit-x1) 0 var(--pl-unit-x4);
  display: flex;
  flex-direction: column;
  min-height: 100%;
  z-index: var(--pl-z-index-level1);
  position: relative;
  width: 380px;
  max-width: 100%;
}
</style>
